import * as d from 'decoders'
import { storageValue } from 'src/helpers/storage'

export type RefreshTokenLockValue = null | {
  readonly id: string
  readonly expiresAt: number
}

export const refreshTokenLockStorage = storageValue<RefreshTokenLockValue>(
  'AuthState:refreshTokenLock',
  (value) => JSON.stringify(value),
  (serializedValue) => {
    const decoder = d.nullable(
      d.object({
        id: d.string,
        expiresAt: d.number,
      })
    )

    return decoder.verify(JSON.parse(serializedValue))
  }
)

export type RefreshTokenLockStorage = typeof refreshTokenLockStorage

// Per-tab unique identifier for the current lock attempt.
// Using a unique ID (not just expiresAt) prevents two tabs that call
// updateLock() within the same millisecond from both thinking they won.
let lockId: string | null = null
let lockTimer: ReturnType<typeof setInterval> | null = null

// Long enough that the lock survives slow 3G + DevTools CPU throttling.
// If the holder tab crashes, other tabs recover after this timeout.
const LOCK_EXPIRES_IN = 15_000

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

function updateLock(): void {
  const id = lockId ?? generateId()
  lockId = id

  refreshTokenLockStorage.setValue({
    id,
    expiresAt: Date.now() + LOCK_EXPIRES_IN,
  })
}

class LockRaceError extends Error {
  constructor() {
    super('lock-race')
  }
}

async function lock(): Promise<void> {
  console.debug('lock')
  lockId = generateId()
  refreshTokenLockStorage.setValue({ id: lockId, expiresAt: Date.now() + LOCK_EXPIRES_IN })

  let settled = false

  return await new Promise<void>((resolve, reject) => {
    if (lockTimer != null) clearInterval(lockTimer)

    lockTimer = setInterval(() => {
      const state = refreshTokenLockStorage.getValue()

      if (state?.id === lockId) {
        // We still own the lock — renew it.
        updateLock()

        if (!settled) {
          console.info(`lock: won`)
          settled = true
          resolve()
        }
        // After settled, keep running — this IS the keepalive.
      } else {
        // Another tab owns the lock.
        if (!settled) {
          // Lost the race before resolving — clean up and reject.
          console.info(`lock: lost race — cleaning up`)

          if (lockTimer != null) {
            clearInterval(lockTimer)
            lockTimer = null
            lockId = null
          }

          settled = true
          reject(new LockRaceError())
        }
        // If already settled (lock was stolen AFTER we acquired it, e.g. due to
        // CPU throttle delaying the keepalive past the expiry), stop renewing —
        // the caller will call unlock() which handles the cleanup.
        else {
          console.info(`lock: keepalive lost ownership after settle — stopping keepalive`)

          if (lockTimer != null) {
            clearInterval(lockTimer)
            lockTimer = null
          }
        }
      }
    }, LOCK_EXPIRES_IN * 0.2)
  })
}

let ensureLockTimer: ReturnType<typeof setInterval> | null = null

function cancelEnsureLock(): void {
  if (ensureLockTimer != null) {
    clearInterval(ensureLockTimer)
  }
}

export async function ensureLock(): Promise<void> {
  console.info('ensureLock')

  for (;;) {
    if (!isLocked()) {
      try {
        console.info('ensureLock: attempting lock()')
        await lock()
        console.info('ensureLock: lock acquired')
        return
      } catch (err) {
        if (!(err instanceof LockRaceError)) throw err
        // Lost the race — another tab won, fall through to wait for it to finish
        console.info('ensureLock: lost race, waiting for lock holder to finish')
      }
    }

    await new Promise<void>((resolve) => {
      cancelEnsureLock()

      ensureLockTimer = setInterval(() => {
        if (!isLocked()) {
          cancelEnsureLock()
          resolve()
        }
      }, LOCK_EXPIRES_IN * 0.5)
    })
  }
}

function isLocked(): boolean {
  const state = refreshTokenLockStorage.getValue()

  return state != null && state.expiresAt > Date.now()
}

function iHaveALock(): boolean {
  const state = refreshTokenLockStorage.getValue()

  return lockId != null && state?.id === lockId
}

export function unlock(): void {
  console.debug('unlock')

  cancelEnsureLock()

  if (iHaveALock() || !isLocked()) {
    refreshTokenLockStorage.setValue(null)
  }

  if (lockTimer != null) {
    clearInterval(lockTimer)

    lockTimer = null
    lockId = null
  }
}
